import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { HTTPError, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type {
  CrmIntegrationSettings,
  IntegrationCrmType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import type { OUTREACH_CRM_EXPORT_OPTIONS } from "../constants";
import type { CrmExportSummary } from "../types";

async function exportLeadsRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<string> {
  const { url } = await post(
    `workspaces/${workspaceId}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );
  return url;
}

async function exportLeadsToCrmIntegrationRequest(
  workspaceId: string,
  integrationKey: keyof CrmIntegrationSettings,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<string | undefined> {
  const { job_id } = await post(
    `workspaces/${workspaceId}/integrations/${integrationKey}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );
  return job_id;
}

async function exportLeadsToOutreachCrmRequest(
  workspaceId: string,
  crmType: IntegrationCrmType,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  type: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS,
  id: string,
): Promise<CrmExportSummary> {
  return (
    await post(
      `workspaces/${workspaceId}/integrations/${crmType}/leads-export`,
      { ...parseLeadsSearchParams(searchParams), [`${type}_id`]: id },
    )
  ).export;
}

export default function useLeadExports() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: exportLeads, isPending: isExportingLeads } = useMutation(
    {
      mutationFn: ({
        searchParams,
      }: {
        searchParams: LeadsSearchParams<SelectedFacetParam>;
      }) => exportLeadsRequest(workspaceId, searchParams),
    },
  );

  const {
    mutateAsync: exportLeadsToCrmIntegration,
    isPending: isExportingLeadsToCrmIntegration,
  } = useMutation({
    mutationFn: ({
      searchParams,
      integrationKey,
    }: {
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      integrationKey: keyof CrmIntegrationSettings;
    }) =>
      exportLeadsToCrmIntegrationRequest(
        workspaceId,
        integrationKey,
        searchParams,
      ),
  });

  const {
    mutateAsync: exportLeadsToOutreachCrm,
    isPending: isExportingLeadsToOutreachCrm,
  } = useMutation({
    mutationFn: ({
      crmType,
      searchParams,
      type,
      id,
    }: {
      crmType: IntegrationCrmType;
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      type: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS;
      id: string;
    }) =>
      exportLeadsToOutreachCrmRequest(
        workspaceId,
        crmType,
        searchParams,
        type,
        id,
      ),
    onSuccess: (_, { crmType }) => {
      const label = `${crmType.charAt(0).toUpperCase()}${crmType.slice(1)}`;

      toast.success(`Successfully exported leads to ${label}`);
    },
    onError: (error, { crmType }) => {
      const label = `${crmType.charAt(0).toUpperCase()}${crmType.slice(1)}`;
      let errorMessage = `Failed to export leads to ${label}`;

      if (error instanceof HTTPError) {
        if (error.type === "Unauthorized") {
          errorMessage = `${label} API token expired. Update your API key to resolve this`;
        } else if (error.type === "Too Many Requests") {
          errorMessage = `${label} rate limit hit. Please try again later`;
        } else if (error.type === "Bad Request") {
          errorMessage =
            "Export failed: No valid leads found. Make sure uploaded leads have an email address";
        }
      }

      toast.error(errorMessage);
    },
  });

  return {
    exportLeads,
    isExportingLeads,
    exportLeadsToCrmIntegration,
    isExportingLeadsToCrmIntegration,
    exportLeadsToOutreachCrm,
    isExportingLeadsToOutreachCrm,
  };
}
